/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends From
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { mapBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import moment from 'moment';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
// Internal Imports
import SickbayResolution from 'linced-app/enums/sickbay-resolution';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

// Reasons the attendance side of a visit can end up not being written.  They
// are returned up to save() and told to the user rather than swallowed, because
// each one leaves the roll disagreeing with the sickbay register.
//
// REASON_DAY_ENDED is the expected one and is reported neutrally: there is no
// period left in the day to attach a movement to, so there is nothing wrong and
// nothing for the user to fix.  The rest mean the roll needs a manual look.
//
// REASON_MOVEMENT_REMOVED is REASON_DAY_ENDED's other half: there is likewise
// no period left to mark, but the movement written for the old time had to come
// off the roll before we found that out, so the roll did change.  It is
// reported separately so the user is never told nothing happened when a mark
// has just been taken away.
const REASON_DAY_ENDED = 'day-ended';
const REASON_MOVEMENT_REMOVED = 'day-ended-movement-removed';
const REASON_NO_PERIOD = 'that time falls outside the timetabled periods';
const REASON_NO_MOVEMENT =
  'their attendance for that period has since been changed by hand';
const REASON_SAVE_FAILED = 'the attendance record could not be saved';

// A movement written by a sickbay visit is only distinguishable from one marked
// by hand by its notes: a check-in note is prefixed, and a release note opens
// with the visit's resolution label.  Both halves are built from these, so the
// note format and _isSickbayMovement can't drift apart.
const CHECK_IN_NOTE_PREFIX = 'Signed in to sickbay: ';
const RESOLUTION_NOTE_SEPARATOR = ' - ';

// joinClauses renders a list of clauses as prose: "a", "a, and b", or
// "a, b, and c".  A plain join() reads fine for the two warnings that exist
// today but turns into "a, and b, and c" the moment a third is added.
function joinClauses(clauses) {
  if (clauses.length < 2) {
    return clauses.join('');
  }
  return `${clauses.slice(0, -1).join(', ')}, and ${clauses.at(-1)}`;
}

export default class SickbayEntry extends Component {
  // Services
  @service
  attendance;

  @service
  attendanceCodes;

  @service
  dates;

  @service
  options;

  @service
  permissions;

  @service
  print;

  @service
  session;

  @service
  store;

  @service
  students;

  @service
  toaster;

  SickbayResolution = SickbayResolution;
  Scopes = Scopes;
  existingVisits = null;
  @tracked learner = null;
  @tracked mode = 'admit';
  @tracked sickbayVisit = null;
  @tracked learners = [];
  sickbayPage = null;
  @tracked teachers = [];
  @tracked dialogOpen = false;
  @tracked dialogOpening = false;
  @tracked saveInProgress = false;
  @tracked generatingMedicalCentreReport = false;

  @alias('attendanceCodes.medicalAbsence.code')
  medicalCode;

  @alias('attendanceCodes.present.code')
  presentCode;

  @alias('attendanceCodes.sickbay.code')
  sickbayCode;

  @alias('attendanceCodes.unknown.code')
  unknownCode;

  @optionAlias('core:staffPermissions:sickbayForTeachers')
  sickbayForTeachers;
  sickbayEntryCreated() {}

  @mapBy('existingVisits', 'learner_id')
  existingVisitsLearnerIds;

  @computed('existingVisitsLearnerIds.[]', 'learner.id')
  get studentCurrentlyInSickbay() {
    return this.existingVisitsLearnerIds.includes(this.get('learner.id'));
  }

  // Lifted from common new-post
  @computed('sickbayPage.tagCategoryIds.[]')
  get tagListClasses() {
    let numCategories = this.get('sickbayPage.tagCategoryIds.length');
    switch (numCategories) {
      case 1:
        return 'flex-100';
      case 2:
        return 'flex-xs-100 flex-gt-xs-45';
      case 3:
        return 'flex-xs-100 flex-gt-xs-30';
      case 4:
        return 'flex-xs-100 flex-gt-xs-30 flex-gt-md-25';
      default:
        // 5 or more categories - limit to 5 per row
        return 'flex-xs-100 flex-gt-xs-30 flex-gt-md-20';
    }
  }

  @computed(
    'mode',
    'sickbayVisit.{learner,checkedInBy,treatedBy,checkInTime,date,notes,checkedOutBy,resolution,releaseNotes,checkOutTime}',
  )
  get isValid() {
    const currentDate = moment().format('YYYY-MM-DD');

    if (
      ['admit', 'edit', 'editReleased'].includes(this.mode) &&
      !(
        this.sickbayVisit.get('learner.id') &&
        this.sickbayVisit.get('checkedInBy.id') &&
        this.sickbayVisit.get('treatedBy.id') &&
        this.sickbayVisit.get('checkInTime') &&
        this.sickbayVisit.get('notes') &&
        this.sickbayVisit.get('date') &&
        this.sickbayVisit.get('date') <= currentDate
      )
    ) {
      return false;
    }
    if (
      ['release', 'editReleased'].includes(this.mode) &&
      !(
        this.sickbayVisit.get('checkedOutBy.id') &&
        this.sickbayVisit.get('resolution') &&
        this.sickbayVisit.get('releaseNotes') &&
        // checkOutTime defaults to the Go zero value 0, which isEmpty() treats
        // as present, so test it for truthiness.
        this.sickbayVisit.get('checkOutTime') &&
        // Compare to the minute, exactly as checkOutTimeValidation does.  The
        // time field only offers minutes, but checkInTime is stamped with
        // seconds when the visit is created, so a raw second-by-second
        // comparison rejects the very time the validation message asks for
        // and leaves Save disabled with no visible error.
        moment
          .unix(this.sickbayVisit.get('checkOutTime'))
          .isSameOrAfter(moment.unix(this.sickbayVisit.get('checkInTime')), 'm')
      )
    ) {
      return false;
    }

    return true;
  }

  @computed('sickbayVisit.checkInTime')
  get checkOutTimeValidation() {
    if (isEmpty(this.sickbayVisit.get('checkInTime'))) {
      return [];
    }

    return [
      {
        message:
          'Time must not be before ' +
          moment.unix(this.sickbayVisit.get('checkInTime')).format('h:mma'),
        validate: (val) => {
          // Same hour and minute are acceptable for save & release entries
          let parsed = moment.unix(this.getTimestamp(val));
          return parsed.isSameOrAfter(
            moment.unix(this.sickbayVisit.get('checkInTime')),
            'm',
          );
        },
      },
    ];
  }

  @computed('sickbayVisit.date')
  get entryDateValidation() {
    if (isEmpty(this.sickbayVisit.get('date'))) {
      return [];
    }
    const currentDate = moment().format('YYYY-MM-DD'); // Get today's date in YYYY-MM-DD format
    return [
      {
        message: 'Cannot forward date entry date',
        validate: (val) => {
          return val <= currentDate;
        },
      },
    ];
  }

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('sickbayVisit.{resolution,notes,releaseNotes}')
  get canDownloadMedicalCentreReport() {
    return (
      (this.sickbayVisit.resolution ===
        SickbayResolution.SENT_TO_MEDICAL_CENTRE ||
        this.sickbayVisit.resolution === SickbayResolution.SENT_TO_HOSPITAL) &&
      !isEmpty(this.sickbayVisit.notes) &&
      !isEmpty(this.sickbayVisit.releaseNotes)
    );
  }

  // _releaseNote builds the note put on the attendance movement for a release.
  // Shared so the resolution label always leads the note, which is what
  // _isSickbayMovement recognises.
  _releaseNote() {
    return `${SickbayResolution.getLabel(
      this.sickbayVisit.get('resolution'),
    )}${RESOLUTION_NOTE_SEPARATOR}${this.sickbayVisit.get('releaseNotes')}`;
  }

  // _isSickbayMovement reports whether a movement on the roll was written by a
  // sickbay visit rather than marked by hand.  Deliberately matches the shape
  // of the note rather than its exact text, so that a visit whose notes or
  // resolution were edited still recognises the movement it wrote earlier.
  _isSickbayMovement(movement) {
    let notes = movement.notes || '';
    if (notes.startsWith(CHECK_IN_NOTE_PREFIX)) {
      return true;
    }
    return SickbayResolution.values.some((resolution) => {
      return notes.startsWith(
        `${resolution.label}${RESOLUTION_NOTE_SEPARATOR}`,
      );
    });
  }

  // addAttendance adds a new attendance record related to a sickbay visit.
  async addAttendance(record, period, time, code) {
    if (isEmpty(period)) {
      // No period specified - most likely past the end of the day.
      return Promise.resolve();
    }

    // A belongsTo takes a record, not the async proxy.
    let personId = await this.sickbayVisit.learner;
    let notes = this._releaseNote();
    if (code === this.sickbayCode) {
      notes = `${CHECK_IN_NOTE_PREFIX}${this.sickbayVisit.notes}`;
    }
    let date = this.sickbayVisit.get('date');
    let timestamp = moment.unix(time);
    let duringPeriod = timestamp.format('HH:mm') > period.startTime;
    if (!duringPeriod) {
      // Not during an attendance period - adjust the time to the start of the
      // next period to avoid any attendance movements outside the period.
      time = this.getTimestamp(period.startTime);
    }

    if (isEmpty(record)) {
      // We don't have a record.  Create one.
      let dateObj = findBy(this.dates.calendar, 'date', date);
      record = this.store.createRecord('attendance/record', {
        schoolId: this.get('session.school'),
        personId,
        authorId: this.get('session.person'),
        code,
        notes,
        dateId: dateObj,
        date: this.sickbayVisit.get('date'),
        periodId: period,
        effectiveFrom: time,
      });
      let movements = [];
      if (duringPeriod) {
        // Add an "unknown" movement for the start of the period.
        movements.push({
          time: this.getTimestamp(period.startTime),
          code: this.unknownCode,
          authorId: this.get('session.person.id'),
          notes: '',
        });
      }
      movements.push({
        time,
        code,
        authorId: this.get('session.person.id'),
        notes,
      });
      record.set('movements', movements);
      return record.save();
    }

    // Otherwise, we have a record; add a new movement to it.  Attendance now
    // blocks the visit save (see save()), so this can be re-run after a failed
    // attempt, and a retry should refresh the movement we already wrote rather
    // than doubling it up.  Time and code alone don't identify it, though: a
    // movement marked by hand can share both, and a record created from the
    // roll puts its first movement on the period's start time - exactly where a
    // gap-time visit is snapped to.  Claiming that one would replace the
    // teacher's or nurse's attribution with the sickbay visit's, so the notes
    // have to say the movement is ours as well.
    let existingMovement = record.movements.find((movement) => {
      return (
        movement.time === time &&
        movement.code === code &&
        this._isSickbayMovement(movement)
      );
    });
    if (existingMovement) {
      existingMovement.setProperties({
        authorId: this.get('session.person.id'),
        notes,
      });
    } else {
      record.movements.createFragment({
        time,
        code,
        authorId: this.get('session.person.id'),
        notes,
      });
    }
    record.setProperties({
      code,
      effectiveFrom: time,
      reason: notes,
      notes,
    });
    return record.save();
  }

  // updateAttendance updates an attendance record related to a sickbay visit,
  // creating one (via addAttendance) if there isn't already one.
  async updateAttendance(oldCode, oldTime, newCode, newTime) {
    let personId = this.sickbayVisit.get('learner.id');
    let date = this.sickbayVisit.get('date');

    if (oldTime === newTime) {
      // The time hasn't changed - so the resolution probably has.
      let period = await this.dates.periodForUnixTime(oldTime);
      if (isEmpty(period)) {
        // No period contains the check-out time, so there's no movement to
        // update.  If nothing follows it either, the day was simply over and
        // there was never anything to write.
        let laterPeriod = await this.dates.periodAfterUnixTime(oldTime);
        return isEmpty(laterPeriod) ? REASON_DAY_ENDED : REASON_NO_PERIOD;
      }
      let record = await this.attendance.recordForPeriod(
        personId,
        date,
        period,
      );
      // Update the note on the relevant movement.
      if (record) {
        let oldMovement = findBy(record.movements, 'time', oldTime);
        if (oldMovement) {
          let notes = this._releaseNote();
          oldMovement.setProperties({
            notes,
            code: newCode,
          });
          record.setProperties({
            effectiveFrom: oldTime,
            notes,
            code: newCode,
            reason: 'Sickbay visit updated',
          });
          return record.save();
        }
        // No matching movement - somebody's probably manually intervened.
      }
      return REASON_NO_MOVEMENT;
    }

    if (oldTime) {
      // We're attempting to update an existing record/movement.
      let oldPeriod = await this.dates.periodForUnixTime(oldTime);
      if (oldPeriod) {
        // The old checkOutTime was during a period, so we should have a
        // matching movement.
        let record = await this.attendance.recordForPeriod(
          personId,
          date,
          oldPeriod,
        );
        // Remove the old movement, as it's not possible to update a
        // movement's time.  We'll replace it with a new one.
        let removedOldMovement = false;
        if (record) {
          let oldMovement = findBy(record.movements, 'time', oldTime);
          if (oldMovement) {
            record.movements.removeObject(oldMovement);
            record.setProperties({
              effectiveFrom: oldTime,
              reason: 'Sickbay visit updated',
            });
            record = await record.save();
            removedOldMovement = true;
          }
          // Otherwise there's no matching movement, but we do have an
          // attendance record - hold onto it so that we can perhaps add a new
          // movement to it below.
        }

        let period = await this.dates.periodForUnixTime(newTime);
        if (isEmpty(period)) {
          // New time isn't within a period.  See if there's a next period.
          period = await this.dates.periodAfterUnixTime(newTime);
          if (isEmpty(period)) {
            // No periods left in the day, so there's nothing to put back.  If
            // we've already taken the old movement off the roll, say so - the
            // roll has changed, even though we couldn't write the new mark.
            return removedOldMovement
              ? REASON_MOVEMENT_REMOVED
              : REASON_DAY_ENDED;
          }
        }
        if (period.id !== oldPeriod.id) {
          // New time is within a different period to the old time.  Fetch
          // the new period's attendance record (if there is one).
          record = await this.attendance.recordForPeriod(
            personId,
            date,
            period,
          );
        }
        return this.addAttendance(record, period, newTime, newCode);
      } else {
        // The old time wasn't during any period.  Check if there is
        // a matching attendance mark for the start of the next period.
        let period = await this.dates.periodAfterUnixTime(oldTime);
        if (isEmpty(period)) {
          return REASON_DAY_ENDED;
        }
        let newPeriod = await this.dates.periodForUnixTime(newTime);
        let newPeriodAfter = await this.dates.periodAfterUnixTime(newTime);
        if (isEmpty(newPeriod) && isEmpty(newPeriodAfter)) {
          return REASON_DAY_ENDED;
        }
        if (isEmpty(newPeriod) && newPeriodAfter.id === period.id) {
          // Both times snap to the start of the same period, so the movement
          // already written is still in the right place.  Nothing to report.
          return;
        }

        // New time is either during a period, or precedes a different
        // period.  Delete old record if it matches old the details.
        let oldRecord = await this.attendance.recordForPeriod(
          personId,
          date,
          period,
        );
        let removedOldRecord = false;
        if (
          oldRecord &&
          isArray(oldRecord.movements) &&
          oldRecord.get('movements.firstObject.code') === oldCode &&
          oldRecord.get('movements.length') === 1
        ) {
          // Next period has a single movement matching the old code.
          // Delete the record.
          oldRecord.set('auditReason', 'Sickbay visit updated');
          await oldRecord.destroyRecord();
          removedOldRecord = true;
        }

        if (isEmpty(newPeriod)) {
          // New time isn't within a period; use the next period.
          newPeriod = newPeriodAfter;
        }
        if (isEmpty(newPeriod)) {
          // New time is after the end of the school day.  As above, a mark we
          // have already cleaned up is a change the user needs to hear about.
          return removedOldRecord ? REASON_MOVEMENT_REMOVED : REASON_DAY_ENDED;
        }
        let record;
        if (newPeriod.id !== period.id) {
          // It's a different period to the one we just cleaned up, so check
          // for an existing attendance record.
          record = await this.attendance.recordForPeriod(
            personId,
            date,
            newPeriod,
          );
        }
        return this.addAttendance(record, newPeriod, newTime, newCode);
      }
    }
  }

  getTimestamp(time) {
    let t = moment(`${this.sickbayVisit.date} ${time}`, 'YYYY-MM-DD HH:mm');
    return t.unix();
  }

  // Lifted from new-post component
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  async _buildPost(visit) {
    let visitRecord = visit || this.sickbayVisit;
    let notes = visitRecord.get('notes') || '';
    let content = notes;
    let details = visitRecord.get('details');
    // Trim any new lines or white spaces for a more accurate comparison
    if (details && details.trim() !== notes.trim()) {
      content += '<br>' + details;
    }

    let resolution = visitRecord.get('resolution');
    if (resolution) {
      let resolutionLabel = SickbayResolution.getLabel(resolution);
      content += `<br><br><b>Resolution:</b> ${resolutionLabel}`;
      let releaseNotes = visitRecord.get('releaseNotes') || '';
      if (releaseNotes) {
        content += '<br><br>' + releaseNotes;
      }
      let releaseDetails = visitRecord.get('releaseDetails');
      if (releaseDetails && releaseDetails.trim() !== releaseNotes.trim()) {
        content += '<br>' + releaseDetails;
      }
    }

    return {
      postType: PostType.POST,
      status: PostStatus.DRAFT,
      schoolId: this.get('session.school'),
      displayDate: visitRecord.get('checkInTime'),
      authorId: this.get('session.person'),
      personId: await visitRecord.get('learnerId'),
      pageId: this.sickbayPage,
      content,
      tagIds: await visitRecord.get('tagIds'),
      subject: `${visitRecord.get('learner.fullName')}, ${moment().format(
        'DD/MM/YYYY LT',
      )}`,
      isSickbay: true,
    };
  }

  // _createLinkedPost creates the Post mirroring a new sickbay visit.  The
  // visit stores the post's id, so this has to succeed before the visit can be
  // saved - unlike the update case, there is no half-saved state to protect.
  async _createLinkedPost() {
    let post = await this.store
      .createRecord('posts/post', await this._buildPost(this.sickbayVisit))
      .save();
    this.sickbayVisit.set('post', post);
    return post;
  }

  // _updateLinkedPost refreshes the Post mirroring an existing sickbay visit.
  // It runs after the visit has been saved, and its failure is deliberately
  // isolated by the caller: the post is a convenience copy, and losing it must
  // not cost us the visit or the learner's attendance.
  async _updateLinkedPost() {
    let post = await this.sickbayVisit.get('post');
    if (isEmpty(post)) {
      return;
    }
    let postParams = await this._buildPost(this.sickbayVisit);
    // Keep the original author: do not overwrite it. Keep the original status.
    delete postParams.authorId;
    postParams.status = post.status;
    post.setProperties(postParams);
    return post.save();
  }

  // _recordMovementAt writes `code` at `time` onto the attendance record for
  // whichever period covers that time, falling back to the next period of the
  // day when the time lands in a gap between periods.  Does nothing when the
  // day has no period left to hang the movement on, which is the case for a
  // check-out after the end of the school day.
  async _recordMovementAt(time, code) {
    let period = await this.dates.periodForUnixTime(time);
    if (isEmpty(period)) {
      period = await this.dates.periodAfterUnixTime(time);
    }
    if (isEmpty(period)) {
      // Nothing left in the day to hang the movement on.
      return REASON_DAY_ENDED;
    }

    let record = await this.attendance.recordForPeriod(
      this.sickbayVisit.get('learner.id'),
      this.sickbayVisit.get('date'),
      period,
    );
    return this.addAttendance(record, period, time, code);
  }

  // _processCheckInAttendance records the attendance movement for a check-in,
  // in jurisdictions which have an "in sickbay" attendance code.  That is every
  // jurisdiction bar NZ MoE, which doesn't track movements at all, and AU NSW,
  // which has no "in sickbay" code and treats the student as present until
  // they're released.
  async _processCheckInAttendance(changes) {
    if (isEmpty(this.sickbayCode) || isEmpty(changes.checkInTime)) {
      return;
    }
    // Student checked in, or check-in time changed, in a school which has an
    // attendance code for "in sickbay".
    let changedCheckInTime = changes.checkInTime;
    if (!isArray(changedCheckInTime)) {
      return;
    }

    if (changedCheckInTime[0]) {
      // The check-in time was corrected rather than set, so the movement we
      // already wrote has to move with it - possibly into a different period.
      return this.updateAttendance(
        this.sickbayCode,
        changedCheckInTime[0],
        this.sickbayCode,
        changedCheckInTime[1],
      );
    }

    // We're adding a checkInTime (i.e. signing the student in).
    return this._recordMovementAt(changedCheckInTime[1], this.sickbayCode);
  }

  // _processCheckOutAttendance records the attendance movement for a release.
  async _processCheckOutAttendance(changes) {
    if (!this.sickbayVisit.get('checkOutTime')) {
      return;
    }
    // The student has been checked out of the sickbay.  checkOutTime can't be
    // cleared, so once it's set, we don't need to worry about it becoming
    // empty again.
    let changedCheckOutTime = changes.checkOutTime;
    let changedResolution = changes.resolution;
    let oldCode = this.medicalCode;
    let newCode = this.medicalCode;
    if (
      !isEmpty(changedResolution) &&
      changedResolution[0] === SickbayResolution.RETURNED_TO_CLASS_BREAK
    ) {
      oldCode = this.presentCode;
    }
    if (
      this.sickbayVisit.resolution === SickbayResolution.RETURNED_TO_CLASS_BREAK
    ) {
      newCode = this.presentCode;
    }

    if (!isArray(changedCheckOutTime)) {
      if (isEmpty(changes.resolution)) {
        return;
      }
      // Time hasn't changed, but resolution has.  Updating the attendance will
      // update the note to match.
      let checkOutTime = this.sickbayVisit.get('checkOutTime');
      return this.updateAttendance(
        oldCode,
        checkOutTime,
        newCode,
        checkOutTime,
      );
    }

    if (changedCheckOutTime[0]) {
      // We're updating an existing checkOutTime.
      return this.updateAttendance(
        oldCode,
        changedCheckOutTime[0],
        newCode,
        changedCheckOutTime[1],
      );
    }

    // We're adding a checkOutTime (signing the student out).
    return this._recordMovementAt(changedCheckOutTime[1], newCode);
  }

  // _processAttendance mirrors the visit onto the learner's attendance record,
  // for jurisdictions which track movements.  Resolves to a REASON_* string
  // when the roll was left untouched, or undefined when there was either
  // nothing to do or the movement was written.
  async _processAttendance(changes, updateMode) {
    if (!this.trackMovements) {
      return;
    }
    let checkInReason = await this._processCheckInAttendance(changes);
    let checkOutReason;
    if (updateMode) {
      checkOutReason = await this._processCheckOutAttendance(changes);
    }
    // Only REASON_* strings are worth reporting; the write paths resolve to
    // saved records, which we don't care about here.  When both halves have
    // something to say we can only show one, so they're weighed rather than
    // taken in order: REASON_DAY_ENDED is the only one asking nothing of the
    // user, and it must not be what hides a reason that does.
    return [
      REASON_NO_PERIOD,
      REASON_NO_MOVEMENT,
      REASON_MOVEMENT_REMOVED,
      REASON_DAY_ENDED,
    ].find((reason) => {
      return [checkInReason, checkOutReason].includes(reason);
    });
  }

  // _discardOrphanedPost cleans up a Post that was created for a visit which
  // then failed to save, so a rejected admission doesn't leave a stray post on
  // the sickbay page.  Best effort only: the save error is the one that matters
  // to the user, so a failure here must not replace it.
  async _discardOrphanedPost(post) {
    if (isEmpty(post)) {
      return;
    }
    try {
      await post.destroyRecord();
    } catch {
      // Nothing more we can do from here.
    }
  }

  // _reportWarnings tells the user about follow-up steps that didn't complete.
  // The visit itself is saved by this point, so these are advisory: the entry
  // is safe, but something alongside it needs a look.
  _reportWarnings(warnings) {
    if (isEmpty(warnings)) {
      return;
    }
    this.toaster.error(
      `Sickbay entry saved, but ${joinClauses(
        warnings,
      )}.  Please check before continuing.`,
    );
  }

  // _isConflict reports whether an adapter error was a 409, which is how the
  // API signals that the learner is already in sickbay.  Any other failure
  // means something else went wrong and shouldn't be reported as a conflict.
  _isConflict(error) {
    if (isEmpty(error) || isEmpty(error.errors)) {
      return false;
    }
    return error.errors.some((e) => `${e.status}` === '409');
  }

  @action
  openDialog() {
    this.dialogOpening = true;
    let now = moment();
    if (this.sickbayPage) {
      this.sickbayPage.reload();
    }
    switch (this.mode) {
      case 'admit':
        this.sickbayVisit = this.store.createRecord(
          'attendance/sickbay-visit',
          {
            schoolId: this.get('session.school'),
            date: now.format('YYYY-MM-DD'),
            authorId: this.get('session.person'),
            treatedById: this.get('session.person'),
            checkedInById: this.get('session.person'),
            checkInTime: now.unix(),
            tagIds: [],
            notes: '',
            details: this.get('sickbayPage.postTemplate'),
          },
        );
        if (this.learner) {
          this.sickbayVisit.set('learner', this.learner);
        }
        break;

      case 'release':
        this.sickbayVisit.set('checkedOutById', this.get('session.person'));
        if (now.format('YYYY-MM-DD') === this.sickbayVisit.date) {
          // Checking out on the day.  Default time to now.
          this.sickbayVisit.set('checkOutTime', now.unix());
        } else {
          // Checking out retrospectively.  Ensure time is empty.
          this.sickbayVisit.set('checkOutTime', null);
        }
        break;
    }
    this.students.all.then((students) => {
      let learners = students.filter((learner) => {
        return !this.existingVisitsLearnerIds.includes(learner.id);
      });
      let teachers = this.store
        .peekAll('person')
        .filter((person) => person.type === PersonType.STAFF);
      this.learners = learners;
      this.teachers = teachers;
      this.dialogOpen = true;
      this.dialogOpening = false;
    });
  }

  @action
  closeDialog() {
    if (!this.sickbayVisit.get('id')) {
      this.sickbayVisit.unloadRecord();
      this.sickbayVisit = null;
    } else {
      // Restore any altered attributes and relationships.
      rollbackRecord(this.sickbayVisit);
    }
    this.dialogOpen = false;
  }

  @action
  async save() {
    this.saveInProgress = true;
    let updateMode = !!this.sickbayVisit.get('id');
    // Grab the changes before processing anything so that we don't lose track
    // of them when the visit is saved.
    let changes = this.sickbayVisit.changedAttributes();
    // Capture the name up front: on a create we clear sickbayVisit before the
    // messages below are built.
    let learnerName =
      this.sickbayVisit.get('learner.fullName') || 'this learner';

    let createdPost = null;
    try {
      if (this.sickbayPage && !updateMode) {
        // A new visit carries the post's id, so the post has to land first.
        createdPost = await this._createLinkedPost();
      }
      await this.sickbayVisit.save();
    } catch (e) {
      await this._discardOrphanedPost(createdPost);
      let conflict = this._isConflict(e);
      this.toaster.error(
        conflict
          ? 'Error - Student is already in sickbay'
          : 'Error - the sickbay entry could not be saved. Please refresh the page and try again. If the error persists, please contact Customer Support.',
      );
      // Retrying a conflict can only fail the same way, so close on that and
      // otherwise leave the dialog up rather than discarding the nurse's work.
      this.dialogOpen = !conflict;
      this.saveInProgress = false;
      return;
    }

    // The visit is recorded.  Attendance is the part that matters most, but
    // it deliberately doesn't block: a release that can't be mirrored onto
    // the roll is still a release that happened, and holding it back leaves
    // the register wrong too.  Instead, it gets its own message, naming the
    // learner and what to do, rather than being folded in with the
    // presentational warnings below.
    let attendanceReason;
    try {
      attendanceReason = await this._processAttendance(changes, updateMode);
    } catch {
      attendanceReason = REASON_SAVE_FAILED;
    }
    if (attendanceReason === REASON_DAY_ENDED) {
      // Expected, not a problem: there's no period left in the day to mark.
      // Reported neutrally so it doesn't read as something gone wrong.
      this.toaster.message(
        `The school day had already ended, so the attendance code for ` +
          `${learnerName} has not been changed.`,
      );
    } else if (attendanceReason === REASON_MOVEMENT_REMOVED) {
      // Same situation, but the mark written for the old time has been taken
      // off the roll on the way through.  Nothing has gone wrong, so this
      // reads neutrally too, but it must name the removal: telling the user
      // the attendance code is unchanged would send them away from a roll
      // that has just lost a mark.
      this.toaster.message(
        `The new time is after the end of the school day, so the sickbay ` +
          `attendance mark for ${learnerName} has been removed and not ` +
          `replaced.  Please check their attendance page.`,
      );
    } else if (attendanceReason) {
      this.toaster.error(
        `Attendance was not updated for ${learnerName}: ` +
          `${attendanceReason}.  Please check their attendance page and ` +
          'update it manually.',
      );
    }

    // What's left is presentational, so it names itself rather than
    // cancelling anything.
    let warnings = [];

    if (this.sickbayPage && updateMode) {
      try {
        await this._updateLinkedPost();
      } catch {
        warnings.push('the linked post was not updated');
      }
    }

    try {
      await this.existingVisits.update();
    } catch {
      warnings.push('the sickbay list may be out of date');
    }

    this._reportWarnings(warnings);

    if (!updateMode) {
      // Clear the local visit too.
      this.sickbayVisit = null;
      this.sickbayEntryCreated();
    }
    this.dialogOpen = false;
    this.saveInProgress = false;
  }

  // saveAndRelease admits and immediately releases a student who didn't stay
  // in sickbay.  It records no attendance movement, by design: the student
  // never left class, so there is nothing for the roll to reflect.
  @action
  async saveAndRelease() {
    this.saveInProgress = true;
    // 1. Create a post and sickbay entry
    // 2. Update the post and sickbay entry to release right after
    let visit = this.sickbayVisit;
    let post = null;

    try {
      if (this.sickbayPage) {
        post = await this._createLinkedPost();
      }

      await visit.save();

      visit.setProperties({
        resolution: SickbayResolution.RETURNED_TO_CLASS_BREAK,
        releaseNotes: 'Did not stay in Sickbay',
        checkOutTime: moment().unix(),
        checkedOutById: this.get('session.person'),
      });
      await visit.save();
    } catch (e) {
      if (!visit.get('id')) {
        // The visit never saved, so any post we created is an orphan.
        await this._discardOrphanedPost(post);
      }
      this.toaster.error(
        this._isConflict(e)
          ? 'Error - Student is already in sickbay'
          : 'Error - the sickbay entry could not be saved. Please refresh the page and try again.',
      );
      this.dialogOpen = false;
      this.saveInProgress = false;
      return;
    }

    // The visit is recorded.  Bringing the post up to date and refreshing the
    // list are both follow-up work, and neither should discard it.
    let warnings = [];

    if (post) {
      try {
        let { content, displayDate } = await this._buildPost(visit);
        post.setProperties({ content, displayDate });
        await post.save();
      } catch {
        warnings.push('the linked post was not updated');
      }
    }

    try {
      await this.existingVisits.update();
    } catch {
      warnings.push('the sickbay list may be out of date');
    }

    this._reportWarnings(warnings);

    this.sickbayVisit = null;
    this.sickbayEntryCreated();
    this.dialogOpen = false;
    this.saveInProgress = false;
  }

  @action
  async toggleTag(tag) {
    // resolve the async hasMany relationship upfront.
    let tags = await this.sickbayVisit.tags;
    let index = tags.indexOf(tag);
    if (index !== -1) {
      tags.splice(index, 1);
    } else {
      tags.push(tag);
    }
  }

  @action
  removeLearner() {
    this.sickbayVisit.set('learner', null);
  }

  @action
  removeCheckedInBy() {
    this.sickbayVisit.set('checkedInBy', null);
  }

  @action
  removeCheckedOutBy() {
    this.sickbayVisit.set('checkedOutBy', null);
  }

  @action
  removeTreatedBy() {
    this.sickbayVisit.set('treatedBy', null);
  }

  @action
  setCheckInTime(time) {
    this.sickbayVisit.set('checkInTime', this.getTimestamp(time));
  }

  @action
  setEntryDate(date) {
    this.sickbayVisit.set('date', date);

    // We need to set the current time to the new date.

    // Existing time extraction
    let currentTime = moment
      .unix(this.sickbayVisit.checkInTime)
      .format('HH:mm');

    // Combine new date with the existing time
    let newCheckInTime = moment(
      date + ' ' + currentTime,
      'YYYY-MM-DD HH:mm',
    ).unix();

    // Update the checkInTime
    this.sickbayVisit.set('checkInTime', newCheckInTime);
  }

  @action
  setCheckOutTime(time) {
    this.sickbayVisit.set('checkOutTime', this.getTimestamp(time));
  }

  @action
  setResolution(resolution) {
    this.sickbayVisit.set('resolution', resolution.value);
  }

  @action
  downloadMedicalCentreReport() {
    this.generatingMedicalCentreReport = true;
    this.sickbayVisit.get('learner').then((learner) => {
      this.print.medicalCentreReport(learner, this.sickbayVisit, () => {
        this.generatingMedicalCentreReport = false;
      });
    });
  }

  @action
  copyToDetails(text, destination) {
    let destinationContent = this.get(destination) || '';
    this.set(destination, text + '\n' + destinationContent);
  }
}
